import { Button, Col, Row, Select } from "antd";
import { useCallback, useState } from "react";

import { PageTitle } from "../../components/AppLayout/components/Content/components";
import { Textarea, TextInput } from "../../components/Form/formComponents";
import { FormManager } from "../../components/Form/FormManager";
import type { FormManagerProps } from "../../components/Form/types";
import { FormLayoutType } from "../../components/Form/types";
import { FormComponent, ValidationType } from "../../components/Form/types/types";
import { NotificationAlertManager } from "../../components/NotificationAlerts/NotificationAlertManager";
import { ResourceName } from "../../core/router/config/ResourceName";
import type { Entity } from "../../core/types/fe/Entity";

interface FormTestModel extends Entity {
    name?: string;
}

export const FormManagerDocs = () => {
    // OUTPUTS
    const [formData, setFormData] = useState<FormTestModel>({});
    const [isValidForm, setIsValidForm] = useState(false);

    const onChangeCallback = useCallback(setFormData, [setFormData]);
    const onValidatedCallback = useCallback(setIsValidForm, [setIsValidForm]);

    // INPUTS
    const [props, setProps] = useState<FormManagerProps<FormTestModel>>({
        name: "DemoForm",
        formData: { id: "AASD33", name: "Pep" },
        formLayout: FormLayoutType.DEFAULT,
        formDefinition: {
            resource: ResourceName.DATA_GROUPS,
            useDataGroup: true,
            tabs: [
                {
                    name: "Demo Tab",
                    fields: [
                        {
                            name: "id",
                            component: FormComponent.TEXT,
                            translationKeys: { label: "Write some text" },
                            attributes: {
                                validationRules: [{ name: ValidationType.VALIDATION_REQUIRED }],
                            },
                        },
                        {
                            name: "name",
                            component: FormComponent.TEXT,
                            translationKeys: { label: "Write some text" },
                            attributes: {
                                validationRules: [{ name: ValidationType.VALIDATION_REQUIRED }],
                            },
                        },
                        {
                            name: "id",
                            component: FormComponent.TEXT,
                            translationKeys: { label: "Write some text" },
                            attributes: {
                                validationRules: [{ name: ValidationType.VALIDATION_REQUIRED }],
                            },
                        },
                        {
                            name: "name",
                            component: FormComponent.TEXT,
                            translationKeys: { label: "Write some text" },
                            attributes: {
                                validationRules: [{ name: ValidationType.VALIDATION_REQUIRED }],
                            },
                        },
                        {
                            name: "id",
                            component: FormComponent.TEXT,
                            translationKeys: { label: "Write some text" },
                            attributes: {
                                validationRules: [{ name: ValidationType.VALIDATION_REQUIRED }],
                            },
                        },
                        {
                            name: "name",
                            component: FormComponent.TEXT,
                            translationKeys: { label: "Write some text" },
                            attributes: {
                                validationRules: [{ name: ValidationType.VALIDATION_REQUIRED }],
                            },
                        },
                    ],
                },
            ],
        },
        onChange: onChangeCallback,
        shouldValidate: 0,
        onValidated: onValidatedCallback,
    });

    return (
        <>
            <p>Documentation can be found on this </p>
            <a
                href="https://aevi.atlassian.net/wiki/spaces/AEVIPAYADMIN/pages/540378853/APA+-+FE+-+Forms+Management#FormManager-Component"
                target="_blank"
                rel="noreferrer"
            >
                Confluence Page
            </a>
            <PageTitle translationKey="Properties" />
            FormManager properties are generic.
            <Row>
                <Col span={3}>
                    <b>Name</b>
                </Col>
                <Col span={3}>
                    <b>Type</b>
                </Col>
                <Col span={6}>
                    <b>Test input</b>
                </Col>
                <Col span={12}>
                    <b>Description</b>
                </Col>
            </Row>
            <Row>
                <Col span={3}>name</Col>
                <Col span={3}>string</Col>
                <Col span={6}>
                    <TextInput
                        value={props.name}
                        onChange={(value) => setProps({ ...props, name: value })}
                        name=""
                        label=""
                        readOnly={false}
                    />
                </Col>
                <Col span={12}>
                    Required.
                    <br />
                    Defines the name of the form.
                    <br />
                    This is important when running UI tests.
                </Col>
            </Row>
            <Row>
                <Col span={3}>formData</Col>
                <Col span={3}>generic object</Col>
                <Col span={6}>
                    <Textarea
                        value={JSON.stringify(props.formData)}
                        onChange={(value) => setProps({ ...props, formData: JSON.parse(value) })}
                        name=""
                        label=""
                        readOnly={false}
                    />
                </Col>
                <Col span={12}>
                    Required.
                    <br />
                    Sets the form data. object must extend from ResourceEntity.
                    <br />
                </Col>
            </Row>
            <Row>
                <Col span={3}>formDefinition</Col>
                <Col span={3}>FormConfiguration</Col>
                <Col span={6}>
                    <Textarea
                        value={JSON.stringify(props.formDefinition)}
                        onChange={(value) => setProps({ ...props, formDefinition: JSON.parse(value) })}
                        name=""
                        label=""
                        readOnly={false}
                    />
                </Col>
                <Col span={12}>
                    Required.
                    <br />
                    Sets the form definition. <br />
                    See{" "}
                    <a
                        target="_blank"
                        href="https://aevi.atlassian.net/wiki/spaces/AEVIPAYADMIN/pages/559743046/APA+-+App+Configurations"
                        rel="noreferrer"
                    >
                        documentation.
                    </a>
                </Col>
            </Row>
            <Row>
                <Col span={3}>formLayout</Col>
                <Col span={3}>FormLayout</Col>
                <Col span={6}>
                    <Select
                        onChange={(selectedOption) => {
                            const { value } = selectedOption as { value: FormLayoutType };
                            if (value === FormLayoutType.TABS) {
                                setProps({ ...props, formLayout: FormLayoutType.TABS });
                            } else if (value === FormLayoutType.DEFAULT) {
                                setProps({ ...props, formLayout: FormLayoutType.DEFAULT });
                            } else {
                                setProps({ ...props, formLayout: FormLayoutType.INLINE });
                            }
                        }}
                        defaultValue={{ value: FormLayoutType.DEFAULT, label: "Default" }}
                        options={[
                            { value: FormLayoutType.DEFAULT, label: "Default" },
                            { value: FormLayoutType.TABS, label: "Tabs" },
                            { value: FormLayoutType.INLINE, label: "Inline" },
                        ]}
                    />
                </Col>
                <Col span={12}>
                    Required.
                    <br />
                    Sets the form layout. <br />
                    Available values from FormLayout are: DEFAULT and TABS
                </Col>
            </Row>
            <Row>
                <Col span={3}>onChange</Col>
                <Col span={3}>{`(values: T) => void`}</Col>
                <Col span={6}>&nbsp;</Col>
                <Col span={12}>
                    Required.
                    <br />
                    It is called when the form data has changed. <br />
                    It receives an object (same type as formData) with all form values updated.
                    <br />
                </Col>
            </Row>
            <Row>
                <Col span={3}>shouldValidate</Col>
                <Col span={3}>number</Col>
                <Col span={6}>
                    <Button onClick={() => setProps({ ...props, shouldValidate: Date.now() })}>Run Validation</Button>
                </Col>
                <Col span={12}>Show form validation errors</Col>
            </Row>
            <Row>
                <Col span={3}>onValidated</Col>
                <Col span={3}>{`(valid: boolean) => void`}</Col>
                <Col span={6}>&nbsp;</Col>
                <Col span={12}>It is called on each update.</Col>
            </Row>
            <PageTitle translationKey="Form Render" />
            <NotificationAlertManager />
            <FormManager {...props} />
            <PageTitle translationKey="Component State" />
            <code>{JSON.stringify({ formData, isValidForm })}</code>
        </>
    );
};
